import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

/** How often the status is polled while a restart is in flight, and how long the UI waits before reporting that readiness did not recover. */
export const API_RESTART_POLL_MS = 3_000;
export const API_RESTART_UI_TIMEOUT_MS = 120_000;

interface ApiServiceStatus {
  readonly service: string;
  readonly state: "healthy" | "restarting" | "degraded";
  readonly ready: boolean;
  readonly inProgress: boolean;
  readonly cooldownRemainingSeconds: number;
  /** Paid Direct Phota calls in flight now (a restart is refused while > 0). */
  readonly activePaidCalls?: number;
  readonly lastRestartId: string | null;
  readonly lastRestartAt: string | null;
  readonly lastRestartBy: string | null;
  readonly lastRestartOutcome: "requested" | "completed" | "degraded" | null;
}

type Phase = "idle" | "confirming" | "requesting" | "waiting" | "success" | "failed";

const STATE_LABEL: Record<ApiServiceStatus["state"], string> = { healthy: "Healthy", restarting: "Restarting", degraded: "Degraded" };

/**
 * Admin "Restart API" (2026-10-01): a controlled restart of the one service `emma-api.service` (the service is fixed on the server — nothing here names it).
 * The restart is handed to a separate helper on the server, so the request returns immediately; this section then polls the real status (which requires
 * readiness, not just "systemd active") and only shows success once the server reports THIS restart as completed and healthy.
 */
export function AdminApiServiceSection() {
  const { user, csrfToken } = useAuth();
  const [status, setStatus] = useState<ApiServiceStatus | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const restartIdRef = useRef<string | null>(null);
  const startedAtRef = useRef(0);
  const submittingRef = useRef(false);

  const loadStatus = useCallback(async (): Promise<ApiServiceStatus | null> => {
    try {
      const next = await api.get<ApiServiceStatus>("/admin/system/api-status");
      setStatus(next);
      return next;
    } catch {
      return null; // the API may be mid-restart: treated as "not reachable yet", never as success
    }
  }, []);

  useEffect(() => {
    if (user?.isPlatformOwner) void loadStatus();
  }, [user?.isPlatformOwner, loadStatus]);

  // Polling while a restart is in flight.
  useEffect(() => {
    if (phase !== "waiting") return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async (): Promise<void> => {
      const next = await loadStatus();
      if (cancelled) return;
      if (next !== null && next.lastRestartId === restartIdRef.current) {
        if (next.lastRestartOutcome === "completed" && next.state === "healthy") {
          setPhase("success");
          return;
        }
        if (next.lastRestartOutcome === "degraded") {
          setError("API restart was requested, but readiness did not recover. Check server logs.");
          setPhase("failed");
          return;
        }
      }
      if (Date.now() - startedAtRef.current > API_RESTART_UI_TIMEOUT_MS) {
        setError("API restart was requested, but readiness did not recover. Check server logs.");
        setPhase("failed");
        return;
      }
      timer = setTimeout(() => void tick(), API_RESTART_POLL_MS);
    };
    timer = setTimeout(() => void tick(), API_RESTART_POLL_MS);
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [phase, loadStatus]);

  if (!user?.isPlatformOwner) return null;

  async function confirmRestart(): Promise<void> {
    if (submittingRef.current) return; // hard guard against a double submit
    submittingRef.current = true;
    setPhase("requesting");
    setError(null);
    try {
      const response = await api.post<{ accepted: boolean; restartId: string }>("/admin/system/restart-api", csrfToken, {});
      restartIdRef.current = response.restartId;
      startedAtRef.current = Date.now();
      setPhase("waiting");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string } | null)?.error === "restart_blocked_active_paid_call") {
        setError("Restart blocked: a Direct Phota paid call is in progress. Try again when it has finished.");
        void loadStatus();
      } else if (err instanceof ApiError && err.status === 429) {
        const retry = (err.body as { retryAfterSeconds?: number } | null)?.retryAfterSeconds;
        setError(`A restart was requested recently — cooldown active${typeof retry === "number" ? `, try again in ${retry}s` : ""}.`);
      } else if (err instanceof ApiError && err.status === 409) {
        setError("A restart is already in progress.");
      } else {
        setError(err instanceof ApiError ? err.message : "The restart request failed.");
      }
      setPhase("failed");
      void loadStatus();
    } finally {
      submittingRef.current = false;
    }
  }

  const busy = phase === "requesting" || phase === "waiting";
  const cooldown = status?.cooldownRemainingSeconds ?? 0;
  const displayState = busy ? "Restarting" : status === null ? "Unknown" : STATE_LABEL[status.state];

  return (
    <div className="card" data-testid="api-service-section">
      <h2>API Service</h2>
      <p className="muted">
        Status: <strong>{displayState}</strong>
        {status?.lastRestartAt && (
          <>
            {" "}
            · Last restart: {new Date(status.lastRestartAt).toLocaleString()}
            {status.lastRestartBy ? ` by ${status.lastRestartBy}` : ""}
          </>
        )}
      </p>
      {phase === "waiting" && <div role="status">Restarting API… Waiting for readiness</div>}
      {phase === "success" && (
        <div className="success-banner" role="status">
          API restarted successfully. Status: Healthy
        </div>
      )}
      {phase === "failed" && error && <div className="error-banner">{error}</div>}
      {phase === "confirming" ? (
        <div role="dialog" aria-label="Restart API confirmation">
          <p>Restart emma-api.service now? Active requests may be interrupted briefly.</p>
          <button type="button" onClick={() => void confirmRestart()}>
            Confirm restart
          </button>{" "}
          <button type="button" onClick={() => setPhase("idle")}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setPhase("confirming")} disabled={busy || cooldown > 0}>
          {busy ? "Restarting..." : "Restart API"}
        </button>
      )}
      {!busy && (status?.activePaidCalls ?? 0) > 0 && <p className="muted">Restart blocked: Direct Phota paid call in progress ({status?.activePaidCalls}).</p>}
      {!busy && cooldown > 0 && <p className="muted">Available again in {cooldown}s (cooldown after the last restart).</p>}
    </div>
  );
}
